import { useEffect, useState, useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { getTicketGroups } from '../../../../../utilities/api'
import useFiltersStore from '../../../../../stores/filtersStore'
import { FilterBar } from './FilterBar'
import { ListingsContainer } from './ListingsContainer'
import { applyTicketGroupFilters } from './applyTicketGroupFilters'
import { LoadingContainer } from '../../../../LoadingContainer'
import useSeatmapStore from '../../../../../stores/seatmapStore'
import { isSectionHidden } from '../../../../../utilities/hiddenSections'

export default function TicketSelectionPanel() {
  const event = useFiltersStore((state) => state.event)
  const filters = useFiltersStore((state) => state.filters)
  const setOffers = useFiltersStore((state) => state.setOffers)
  const allTicketGroups = useFiltersStore((state) => state.ticketGroups)
  const setTicketGroups = useFiltersStore((state) => state.setTicketGroups)
  const loadingTicketGroups = useFiltersStore(
    (state) => state.loadingTicketGroups
  )
  const setLoadingTicketGroups = useFiltersStore(
    (state) => state.setLoadingTicketGroups
  )
  const setFilters = useFiltersStore((state) => state.setFilters)
  const seatmapSections = useSeatmapStore((state) => state.data?.sections)

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)

  const [isLoadingTickets, setIsLoadingTickets] = useState(true)
  const [isMappingCreated, setIsMappingCreated] = useState(false)
  const [isScheduled, setIsScheduled] = useState(false)
  const [scheduledTime, setScheduledTime] = useState(false)
  const [needToUnlock, setNeedToUnlock] = useState(false)
  const [quickpicks, setQuickpicks] = useState([])
  const [soldout, setSoldout] = useState(false)

  const filterTimeoutRef = useRef(null)
  const prevAccessCodesRef = useRef(filters?.accessCodes || [])

  const fetchAllTicketGroups = async () => {
    setLoadingTicketGroups(true)
    try {
      const data = {
        event: event ? { id: event.id, uuid: event.uuid } : event,
        quantity: 0,
        offerIds: [],
        priceRange: [0, 500],
        accessCodes: filters.accessCodes,
        accessible: false,
        sort: 'bestseat',
        returnLocked: true,
      }
      const res = await getTicketGroups(data)
      setIsScheduled(res.data.isScheduled)
      setScheduledTime(res.data.scheduledTime)
      setIsMappingCreated(true)
      setNeedToUnlock(res.data.needToUnlock)

      if (res.data.soldout) {
        setSoldout(true)
        setTicketGroups([])
      } else {
        setTicketGroups(res.data.ticketGroups)
        setOffers(res.data?.offers)
        setSoldout(false)
      }
    } catch (error) {
      console.error('Error fetching all ticket groups:', error)
      setTicketGroups([])
    } finally {
      setLoadingTicketGroups(false)
    }
  }

  const applyLocalFilters = useCallback(
    (ticketGroups, filterParams) =>
      applyTicketGroupFilters(
        (ticketGroups || []).filter(
          (group) => !isSectionHidden(seatmapSections, group?.sectionId)
        ),
        filterParams
      ),
    [seatmapSections]
  )

  const applyFiltersWithDelay = useCallback(
    (newFilters) => {
      setIsLoadingTickets(true)
      if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current)
      const startTime = Date.now()
      const filteredTickets = applyLocalFilters(allTicketGroups, newFilters)
      const remainingTime = Math.max(600 - (Date.now() - startTime), 0)
      filterTimeoutRef.current = setTimeout(() => {
        setQuickpicks(filteredTickets)
        setIsLoadingTickets(false)
      }, remainingTime)
    },
    [allTicketGroups, applyLocalFilters]
  )

  useEffect(() => {
    if (!event) return
    fetchAllTicketGroups()
  }, [event])

  useEffect(() => {
    if (!event) return
    const offersParam = searchParams.get('offers')
    const selectedOfferIds = offersParam?.trim()
      ? offersParam
          .split(',')
          .filter((id) => id.trim())
          .map((id) => parseInt(id, 10))
      : []
    const accessCodes =
      searchParams
        .get('access_code')
        ?.split(',')
        .filter((code) => code.trim()) || []

    setFilters({
      quantity: parseInt(searchParams.get('quantity')) || 2,
      sort: searchParams.get('sort') === 'bestseat' ? 'bestseat' : 'price',
      accessible: searchParams.get('accessible') === 'true',
      accessCodes,
      selectedOfferIds,
    })
  }, [location.search, event, setFilters])

  useEffect(() => {
    if (!filters?.accessCodes || allTicketGroups.length === 0) return
    const currentAccessCodes = filters.accessCodes
    const prevAccessCodes = prevAccessCodesRef.current
    const accessCodesChanged =
      JSON.stringify([...currentAccessCodes].sort()) !==
      JSON.stringify([...prevAccessCodes].sort())
    if (accessCodesChanged) {
      prevAccessCodesRef.current = currentAccessCodes
      fetchAllTicketGroups()
    }
  }, [filters?.accessCodes, allTicketGroups])

  useEffect(() => {
    if (allTicketGroups.length > 0 && filters) {
      const currentAccessCodes = filters.accessCodes || []
      const prevAccessCodes = prevAccessCodesRef.current
      const accessCodesChanged =
        JSON.stringify([...currentAccessCodes].sort()) !==
        JSON.stringify([...prevAccessCodes].sort())
      if (!accessCodesChanged) applyFiltersWithDelay(filters)
    } else if (!loadingTicketGroups) {
      setIsLoadingTickets(false)
    }
  }, [filters, allTicketGroups, applyFiltersWithDelay, loadingTicketGroups])

  useEffect(() => {
    return () => {
      if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current)
    }
  }, [])

  return (
    <section className="spp-listings-card">
      {loadingTicketGroups ? (
        <LoadingContainer />
      ) : (
        <>
          {!isScheduled && isMappingCreated ? (
            <FilterBar
              quickpicks={quickpicks}
              isLoadingTickets={isLoadingTickets}
            />
          ) : null}

          <ListingsContainer
            isMappingCreated={isMappingCreated}
            isScheduled={isScheduled}
            scheduledTime={scheduledTime}
            isLoadingTickets={isLoadingTickets}
            quickpicks={quickpicks}
            soldout={soldout}
            reloadQuickPicks={fetchAllTicketGroups}
            needToUnlock={needToUnlock}
          />
        </>
      )}
    </section>
  )
}
